/*
 * MOTION DESIGN LTD CONFIDENTIAL
 *
 * [2026] Motion Design Ltd All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Motion Design Ltd. The intellectual and technical concepts contained
 * herein are proprietary to Motion Design Ltd. and may be covered by N.Z.
 * and Foreign Patents, patents in process, and are protected by trade secret
 * or copyright law. Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written permission
 * is obtained from Motion Design Ltd.
 */

import {useAuthState} from '@/auth/authContext'
import {API} from '@/constants/api'
import {DEFAULT_SYSTEM_TIME_ZONE} from '@/helpers/dateTime'
import {handleErrorAsToast} from '@/helpers/errors'
import React, {createContext, ReactNode, useContext, useEffect, useMemo, useState} from 'react'

interface SystemTimeZoneContextValue {
    systemTimeZone: string
    setSystemTimeZone: (timeZone: string) => void
}

const SystemTimeZoneContext = createContext<SystemTimeZoneContextValue>({
    systemTimeZone: DEFAULT_SYSTEM_TIME_ZONE,
    setSystemTimeZone: () => undefined,
})

export function SystemTimeZoneProvider({children}: {children: ReactNode}) {
    const [authState] = useAuthState()
    const [systemTimeZone, setSystemTimeZone] = useState(DEFAULT_SYSTEM_TIME_ZONE)
    const userId = authState.userInfo?.id

    useEffect(() => {
        if (!userId) {
            setSystemTimeZone(DEFAULT_SYSTEM_TIME_ZONE)
            return
        }
        let current = true
        API.APP.RELEASES.displaySettings()
            .then((settings) => current && setSystemTimeZone(settings.systemTimeZone))
            .catch((error) => {
                if (current) handleErrorAsToast(error)
            })
        return () => {
            current = false
        }
    }, [userId])

    const value = useMemo(() => ({systemTimeZone, setSystemTimeZone}), [systemTimeZone])

    return <SystemTimeZoneContext.Provider value={value}>{children}</SystemTimeZoneContext.Provider>
}

export function useSystemTimeZone(): SystemTimeZoneContextValue {
    return useContext(SystemTimeZoneContext)
}
